import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_CONTACT_EMAIL = "innovation.labs@jadeglobal.com";

/**
 * NOTE:
 * - Put logos in: /public/assets/accelerators/
 *   Example:
 *     /public/assets/accelerators/sn-reviewgen.png
 *     /public/assets/accelerators/ragnative.png
 *
 * - Put downloadable files in: /public/downloads/
 *   Example:
 *     /public/downloads/SN-ReviewGen.zip
 *     /public/downloads/RagNative.zip
 *
 * If you use API-driven downloads later, replace downloadUrl with your endpoint.
 *
 * - "accentKey" controls the category rail / badge color (see CATEGORY_COLORS).
 *   Add a key per new category, or it falls back to the default accent.
 */
const ACCELERATORS = [
  {
    id: "ragnative",
    title: "RagNative",
    category: "RAG Platform",
    accentKey: "default",
    tagline:
      "No-code, private RAG chatbot platform with full control and data sovereignty",
    logoUrl: "/assets/accelerators/ragnative.png",
    downloadUrl: "/downloads/RagNative.zip",
    contactEmail: DEFAULT_CONTACT_EMAIL,
    overview: [
      "RagNative enables enterprises to build, deploy, and operate AI chatbots securely within their own environment—without complexity or data risk.",
      "It is a packaged, no-code Retrieval-Augmented Generation (RAG) platform that allows organizations to use their own data, run on their own infrastructure, and bring their choice of LLMs (chat and embedding models).",
      "The platform handles the entire RAG lifecycle—knowledge ingestion, configurable chunking and vectorization, semantic search, and response generation—while giving full transparency and control over how answers are generated.",
      "It can be deployed on-premises or in private cloud environments, with zero dependency on external systems, and delivers chatbots instantly via shareable URLs or iFrame embeds.",
    ],
    benefits: [
      {
        title: "Absolute Data Sovereignty & Security",
        points: [
          "Zero Data Leakage: Data never leaves your environment; reduces risk associated with public/cloud-hosted AI.",
          "Built for Compliance: Ideal for regulated industries requiring strict data residency and privacy control.",
        ],
      },
      {
        title: "Lightning-Fast Time to Value",
        points: [
          "From Data to Chatbot in Minutes: No-code interface accelerates deployment vs. months of engineering.",
          "Instant Integration: Shareable URLs or iFrame embeds for quick rollout across portals and intranets.",
        ],
      },
      {
        title: "Total Control (No “Black Boxes”)",
        points: [
          "Bring Your Own Model: Swap LLMs/embeddings over time to stay best-in-class.",
          "Full Transparency: Control chunking, prompting, and context; answers can include source attribution.",
        ],
      },
      {
        title: "Eradicate Technical Complexity",
        points: [
          "No AI Experts Required: Platform manages backend orchestration so teams focus on business outcomes.",
        ],
      },
      {
        title: "High-Trust, Reliable Outputs",
        points: [
          "Goodbye Hallucinations: Ground responses in your documents and policies with tailored prompting.",
        ],
      },
    ],
  },
   {
    id: "sn-reviewgen",
    title: "SN-ReviewGen",
    category: "ServiceNow",
    accentKey: "servicenow",
    tagline: "AI-powered code review for ServiceNow development",
    logoUrl: "/assets/accelerators/sn-reviewgen.png",
    downloadUrl: "/downloads/SN-ReviewGen.zip",
    contactEmail: DEFAULT_CONTACT_EMAIL,
    overview: [
      "ReviewGen is an AI powered code review solution designed seamlessly for ServiceNow.",
      "Think of ReviewGen as an automatic quality checker for your code. It removes the need for slow, manual reviews by instantly checking scripts, business rules and script includes for issues before they go live.",
      "This helps teams deliver work faster, reduce errors and rework, lower risk in production, and maintain consistent quality—without adding more people or slowing down delivery.",
      "Unlike traditional linters or static rule checkers, ReviewGen understands code intent and context, suggests meaningful improvements (not just syntax fixes), enforces best practices/performance/security standards, and acts as an AI teammate reviewing code instantly and consistently.",
    ],
    benefits: [
      {
        title: "Transform Development Speed (Velocity)",
        points: [
          'Instant Feedback: Eliminate the "waiting game" of manual peer reviews. Developers get comprehensive feedback in seconds.',
          "Remove Bottlenecks: Reduce reliance on senior developers for repetitive review work so they can focus on innovation and architecture.",
        ],
      },
      {
        title: "Guarantee High-Performance Code (Quality)",
        points: [
          "Beyond Syntax: Catch logic flaws, security vulnerabilities, and performance bottlenecks that basic tools often miss.",
          "Total Consistency: Enforce unified coding standards across the org—every script reaches a consistent quality bar.",
        ],
      },
      {
        title: "Future-Proof Your Platform (Stability)",
        points: [
          'Slash Technical Debt: Identify risky "anti-patterns" before they reach production to simplify maintenance and upgrades.',
          "Enterprise Security: Scan for vulnerabilities and align scripts with strong security protocols.",
        ],
      },
      {
        title: "Empower Your Developers (Growth)",
        points: [
          "Continuous Learning: Constructive coaching in real time to reinforce best practices.",
          "Reduced Friction: Minimize back-and-forth between developers and reviewers for a more agile workflow.",
        ],
      },
      {
        title: "Measure Your Success (Visibility)",
        points: [
          "ROI Dashboard: Track review activity, severity of issues caught, and estimate hours/cost saved by automation.",
        ],
      },
    ],
  },
  {
    id: "SAP-reviewgen",
    title: "SAP-ReviewGen",
    category: "SAP",
    accentKey: "SAP",
    tagline: "AI-powered code review for SAP development",
    logoUrl: "/assets/accelerators/sn-reviewgen.png",
    downloadUrl: "",
    contactEmail: DEFAULT_CONTACT_EMAIL,
    overview: [
      "This tool extends AI-powered code review to SAP ABAP development through SAP's third-party API integration. It analyzes ABAP source code alongside the results of SAP's existing static code analysis tools, such as ATC and Code Inspector, providing the AI with both the code and its associated findings as contextual input. By leveraging this combined context, the AI delivers a more comprehensive, prioritized, and context-aware code review instead of evaluating the code in isolation.",
      "How it works",
      "1. Submit your source code and existing code analysis ATC results.",
      "2. Analyze both inputs together using SAP-reviewgen.",
      "3. Correlate code with the ATC findings to identify deeper insights and eliminate redundant issues or findings.",
      "4. Prioritize findings based on severity, impact, and context.",
      "5. Receive a single, consolidated report with actionable recommendations.",
    ],
    benefits: [
      {
        title: "Instant feedback, no manual review wait",
        points: [
          "Results appear in seconds, right inside the editor — so issues get caught while the code is still fresh, not days later in a review thread.",
        ],
      },
      {
        title: "Goes beyond fixed rules",
        points: [
          "Every issue is categorized Critical, Medium, or Low, so developers know what to fix first instead of facing a wall of warnings. Rule-based checks catch known patterns, but miss context-dependent issues that depend on how the code is actually used. The AI looks at the full script, not just isolated rules, so it can catch what fixed checks can't.",
        ],
      },
      {
        title: "Fits into existing SAP workflows",
        points: [
          "Built on SAP's third-party API integration, so it slots into your current ABAP development process rather than requiring a new standalone tool or separate review step.",
        ],
      },
      {
        title: "Bring your own LLM",
        points: [
          "Clients can plug in their own LLM instead of the default one  experience, without changing how the integration or review workflow function.",
        ],
      },
    ],
  },  
  {
    id: "Oracle-reviewgen",
    title: "Oracle-ReviewGen",
    category: "Oracle",
    accentKey: "oracle",
    tagline: "AI-powered code review for Oracle Database development",
    logoUrl: "/assets/accelerators/sn-reviewgen.png",
    downloadUrl: "",
    contactEmail: DEFAULT_CONTACT_EMAIL,
    overview: [
      "ReviewGen is an AI-powered code review plugin for Oracle SQL Developer that integrates directly into the SQL Developer toolbar. It reviews your PL/SQL code to identify risky patterns, performance bottlenecks, and coding best-practice violations before they reach production. Beyond detecting code quality concerns, ReviewGen provides contextual explanations of their potential impact, enabling developers to understand the reasoning behind each finding and make informed improvements.",
      "How it works",
      "1. Open any PL/SQL script in the SQL Developer editor.",
      "2. Click the ReviewGen icon in the toolbar.",
      "3. The script is submitted for code review and analysis.",
      "4. Get  instant review — Line, Severity, and a review Comment for each code segment.",
    ],
    benefits: [
      {
        title: "Instant feedback, no manual review wait",
        points: [
          "Results appear in seconds, right inside the editor — so issues get caught while the code is still fresh, not days later in a review thread.",
        ],
      },
      {
        title: "Prioritized, not just flagged",
        points: [
          "Every issue is categorized Critical, Medium, or Low, so developers know what to fix first instead of facing a wall of warnings.",
        ],
      },
      {
        title: "Catches what's easy for humans to miss",
        points: [
          "SQL injection risk, unclosed cursors, row-by-row processing, unvalidated inputs — the kind of issues that cause production incidents but slip through a quick read-through.",
        ],
      },
      {
        title: "Zero workflow disruption",
        points: [
          " No separate tool, no copying code elsewhere. Review happens inside SQL Developer as part of writing the code, not a separate step.",
        ],
      },
      {
        title: "Bring your own LLM",
        points: [
          "Clients can plug in their own LLM instead of the default one — same toolbar experience, different engine underneath.",
        ],
      },
    ],
  },
];

/* Per-category accent. Add a key here when a new category is introduced;
   unmatched categories fall back to "default". */
const CATEGORY_COLORS = {
  servicenow: {
    rail: "#1F8A6F",
    soft: "rgba(31,138,111,0.10)",
    text: "#136B53",
    ring: "rgba(31,138,111,0.28)",
  },
  rag: {
    rail: "#3730E0",
    soft: "rgba(55,48,224,0.09)",
    text: "#3730E0",
    ring: "rgba(55,48,224,0.28)",
  },
  oracle: {
    rail: "#005c99",
    soft: "#005c992d",
    text: "#005c99",
    ring: "#005c99"
  },
  SAP: {
    rail: "#28decf",
    soft: "rgba(48, 224, 209, 0.18)",
    text: "#21c8da",
    ring: "rgba(27, 180, 124, 0.28)",
  },
  default: {
    rail: "#2563eb",
    soft: "#2564eb2e",
    text: "#2563eb",
    ring: "#2564eb53",
  },
};

function getAccent(a) {
  return CATEGORY_COLORS[a.accentKey] || CATEGORY_COLORS.default;
}

/* ---------------- UI Helpers ---------------- */

function CategoryBadge({ label, accent }) {
  return (
    <span
      className="acc-badge"
      style={{
        color: accent.text,
        background: accent.soft,
        borderColor: accent.ring,
      }}
    >
      {label}
    </span>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7 1.5V9M7 9L4 6M7 9L10 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.5 10.5V11.5C1.5 12.0523 1.94772 12.5 2.5 12.5H11.5C12.0523 12.5 12.5 12.0523 12.5 11.5V10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="1.5"
        y="3"
        width="11"
        height="8"
        rx="1.4"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d="M2 4L7 7.5L12 4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M11 11L14.5 14.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path
        d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ---------------- Details Modal ---------------- */

function DetailsModal({ accelerator, accent, onClose, onCopyEmail }) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Lock scroll + focus the dialog on open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Esc to close + basic focus trap
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'button, a[href], [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!accelerator) return null;
  const a = accelerator;

  return (
    <div
      className="acc-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="acc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`acc-modal-title-${a.id}`}
        ref={dialogRef}
        style={{ "--rail-color": accent.rail }}
      >
        <div className="acc-modal-rail" aria-hidden="true" />

        <div className="acc-modal-body">
          <div className="acc-modal-head">
            <div className="acc-modal-head-main">
              <div className="acc-meta-row">
                <div id={`acc-modal-title-${a.id}`} className="acc-modal-title">
                  {a.title}
                </div>
                <CategoryBadge label={a.category} accent={accent} />
              </div>
              <div className="acc-tagline">{a.tagline}</div>
            </div>
            <button
              className="acc-modal-close"
              type="button"
              onClick={onClose}
              ref={closeBtnRef}
              aria-label="Close details"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="acc-modal-scroll">
            <div className="acc-section">
              <div className="acc-h">Summary</div>
              <div className="acc-paras">
                {(a.overview || []).map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </div>

            <div className="acc-section">
              <div className="acc-h">Benefits</div>
              <div className="acc-benefits">
                {(a.benefits || []).map((b) => (
                  <div className="acc-benefit" key={b.title}>
                    <div className="acc-benefit-title">{b.title}</div>
                    <ul className="acc-ul">
                      {(b.points || []).map((pt, i) => (
                        <li key={i}>{pt}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="acc-modal-footer">
            <div className="acc-contact">
              <span className="acc-muted">Contact</span>
              <span className="acc-dot" aria-hidden="true">
                ·
              </span>
              <button
                type="button"
                className="acc-linkbtn"
                onClick={() =>
                  onCopyEmail(a.contactEmail || DEFAULT_CONTACT_EMAIL)
                }
                title="Copy email"
              >
                {a.contactEmail || DEFAULT_CONTACT_EMAIL}
              </button>
            </div>

            <div className="acc-modal-footer-actions">
              {a.downloadUrl &&(
                <a
                  className="btn primary"
                  href={a.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  download
                >
                  <DownloadIcon />
                  Download
                </a>
              )}
              <a
                className="btn subtle"
                href={`mailto:${a.contactEmail || DEFAULT_CONTACT_EMAIL}?subject=${encodeURIComponent(
                  `Request: ${a.title} Accelerator`,
                )}`}
              >
                <MailIcon />
                Email now
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function AcceleratorsPage() {
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [toast, setToast] = useState("");

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ACCELERATORS;
    return ACCELERATORS.filter((a) => {
      const hay = [
        a.title,
        a.category,
        a.tagline,
        ...(a.overview || []),
        ...(a.benefits || []).map((b) => b.title),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
  }, [q]);

  const active = useMemo(
    () => ACCELERATORS.find((a) => a.id === activeId) || null,
    [activeId],
  );

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(window.__accToastTimer);
    window.__accToastTimer = window.setTimeout(() => setToast(""), 1800);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Email copied");
    } catch {
      showToast("Couldn’t copy — try again");
    }
  };

  return (
    <div className="acc-page">
      <style>{styles}</style>

      <div className="acc-header">
        <div>
          <div className="acc-eyebrow">Innovation Labs</div>
          <div className="acc-title">Accelerators</div>
          <div className="acc-subtitle">
            Ready-to-use building blocks for faster delivery in Jade Orion
          </div>
        </div>

        <div className="acc-actions">
          <div className="acc-search">
            <span className="acc-search-icon" aria-hidden="true">
              <SearchIcon />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search accelerators…"
              aria-label="Search accelerators"
            />
            {q ? (
              <button
                className="acc-search-clear"
                type="button"
                onClick={() => setQ("")}
                aria-label="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="acc-result-row">
        <span>
          {list.length} {list.length === 1 ? "accelerator" : "accelerators"}
          {q ? <> matching “{q}”</> : null}
        </span>
      </div>

      <div className="acc-grid" role="list">
        {list.map((a) => {
          const accent = getAccent(a);
          const initials =
            (a.title || "?")
              .split(/\s+/)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join("") || "A";
          const firstBenefit = a.benefits?.[0];

          return (
            <div
              className="acc-card"
              role="listitem"
              key={a.id}
              style={{ "--rail-color": accent.rail }}
            >
              <div className="acc-rail" aria-hidden="true" />

              <div className="acc-card-body">
                <div className="acc-card-top">
                  <div className="acc-logo-wrap">
                    {a.logoUrl ? (
                      <img
                        className="acc-logo"
                        src={a.logoUrl}
                        alt={`${a.title} logo`}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fb =
                            e.currentTarget.parentElement?.querySelector(
                              ".acc-logo-fallback",
                            );
                          if (fb) fb.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="acc-logo-fallback"
                      style={{ display: a.logoUrl ? "none" : "flex" }}
                    >
                      {initials}
                    </div>
                  </div>

                  <div className="acc-meta">
                    <div className="acc-meta-row">
                      <div className="acc-card-title">{a.title}</div>
                      <CategoryBadge label={a.category} accent={accent} />
                    </div>
                    <div className="acc-tagline">{a.tagline}</div>
                  </div>
                </div>

                {firstBenefit ? (
                  <div className="acc-teaser">
                    <span className="acc-teaser-dot" aria-hidden="true" />
                    <span className="acc-teaser-text">
                      {firstBenefit.title}
                    </span>
                  </div>
                ) : null}

                <div className="acc-cta-row">
             {a.downloadUrl &&(
                <a
                  className="btn primary"
                  href={a.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  download
                >
                  <DownloadIcon />
                  Download
                </a>
              )}

                  <button
                    className="btn ghost"
                    type="button"
                    onClick={() => setActiveId(a.id)}
                    aria-haspopup="dialog"
                  >
                    More details
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!list.length ? (
        <div className="acc-empty">
          <div>
            <div className="acc-empty-title">No accelerators found</div>
            <div className="acc-empty-sub">
              Nothing matches “{q}”. Try a different keyword or category.
            </div>
          </div>
          <button className="btn ghost" onClick={() => setQ("")} type="button">
            Clear search
          </button>
        </div>
      ) : null}

      {active ? (
        <DetailsModal
          accelerator={active}
          accent={getAccent(active)}
          onClose={() => setActiveId(null)}
          onCopyEmail={copyToClipboard}
        />
      ) : null}

      {toast ? <div className="acc-toast">{toast}</div> : null}
    </div>
  );
}

/* ---------------- styles (scoped to this page) ---------------- */

const styles = `
  :root {
    --acc-brand: #2563eb;
    --acc-brand-dark: #2563eb;
    --acc-text: #14161F;
    --acc-muted: #6B7080;
    --acc-border: #e5e7eb;
    --acc-bg: #F7F7FB;
    --acc-card: #ffffff;
    --acc-shadow: 0 1px 2px rgba(20,22,31,0.04), 0 12px 24px -8px rgba(20,22,31,0.08);
    --acc-shadow-hover: 0 1px 2px rgba(20,22,31,0.05), 0 18px 32px -10px rgba(20,22,31,0.14);
  }

  .acc-page{
    padding: 28px 24px 40px;
    font-family: "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    color: var(--acc-text);
    background: transparent;
  }

  .acc-header{
    display:flex;
    justify-content:space-between;
    align-items:flex-end;
    gap: 16px;
    margin-bottom: 6px;
  }

  .acc-eyebrow{
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--acc-brand);
    margin-bottom: 6px;
  }

  .acc-title{
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.4px;
    line-height: 1.1;
  }

  .acc-subtitle{
    margin-top: 5px;
    color: var(--acc-muted);
    font-size: 13.5px;
  }

  .acc-actions{
    display:flex;
    gap: 10px;
    align-items:center;
  }

  .acc-search{
    display:flex;
    align-items:center;
    gap: 8px;
    background: #fff;
    border: 1px solid var(--acc-border);
    border-radius: 12px;
    padding: 10px 12px;
    min-width: 300px;
    box-shadow: 0 1px 2px rgba(20,22,31,0.03);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .acc-search:focus-within{
    border-color: var(--acc-brand);
    box-shadow: 0 0 0 3px rgba(55,48,224,0.12);
  }
  .acc-search-icon{ color: var(--acc-muted); display:flex; }
  .acc-search input{
    border: none;
    outline: none;
    width: 100%;
    font-size: 13.5px;
    color: var(--acc-text);
    background: transparent;
  }
  .acc-search input::placeholder{ color: #9CA0AE; }

  .acc-search-clear{
    border: none;
    background: #F0F0F5;
    color: var(--acc-muted);
    width: 20px;
    height: 20px;
    border-radius: 999px;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    display:flex;
    align-items:center;
    justify-content:center;
    flex: 0 0 auto;
  }
  .acc-search-clear:hover{ background: #E4E4ED; color: var(--acc-text); }

  .acc-result-row{
    color: var(--acc-muted);
    font-size: 12.5px;
    font-weight: 600;
    margin: 14px 2px 16px;
  }

  .acc-grid{
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 16px;
  }

  .acc-grid > .acc-card{
    flex: 1 1 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }

  @media (max-width: 980px){
    .acc-grid > .acc-card{ flex: 1 1 100%; max-width: 100%; }
    .acc-search{ min-width: 220px; flex: 1 1 auto; }
    .acc-header{ align-items: stretch; flex-direction: column; }
    .acc-actions{ width: 100%; }
  }

  .acc-card{
    position: relative;
    display: flex;
    height:190px;
    background: var(--acc-card);
    border: 1px solid var(--acc-border);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--acc-shadow);
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .acc-card:hover{
    box-shadow: var(--acc-shadow-hover);
    transform: translateY(-1px);
  }

  .acc-rail{
    width: 4px;
    flex: 0 0 auto;
    background: var(--rail-color, var(--acc-brand));
  }

  .acc-card-body{
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .acc-card-top{
    display:flex;
    gap: 14px;
    padding: 18px 18px 0 18px;
  }

  .acc-logo-wrap{
    width: 56px;
    height: 56px;
    border-radius: 12px;
    border: 1px solid var(--acc-border);
    background: linear-gradient(180deg, #ffffff, #F7F7FB);
    display:flex;
    align-items:center;
    justify-content:center;
    flex: 0 0 auto;
    overflow:hidden;
  }

  .acc-logo{
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }

  .acc-logo-fallback{
    width: 100%;
    height: 100%;
    display:flex;
    align-items:center;
    justify-content:center;
    font-weight: 800;
    color: var(--acc-text);
    letter-spacing: 0.5px;
    font-size: 15px;
    background: var(--acc-bg);
  }

  .acc-meta{ flex: 1 1 auto; min-width: 0; }

  .acc-meta-row{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap: 10px;
  }

  .acc-card-title{
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .acc-badge{
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    padding: 4px 9px;
    border-radius: 999px;
    border: 1px solid transparent;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .acc-tagline{
    margin-top: 5px;
    color: var(--acc-muted);
    font-size: 13px;
    line-height: 1.4;
  }

  .acc-teaser{
    display:flex;
    align-items:center;
    gap: 7px;
    margin: 12px 18px 0;
    padding: 9px 12px;
    background: var(--acc-bg);
    border: 1px solid var(--acc-border);
    border-radius: 10px;
    font-size: 12.5px;
    color: #43485A;
  }
  .acc-teaser-dot{
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--rail-color, var(--acc-brand));
    flex: 0 0 auto;
  }
  .acc-teaser-text{
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .acc-cta-row{
    display:flex;
    gap: 10px;
    flex-wrap: wrap;
    padding: 14px 18px 18px;
  }

  .btn{
    border: 1px solid var(--acc-border);
    background: #fff;
    color: var(--acc-text);
    border-radius: 10px;
    padding: 9px 13px;
    font-weight: 700;
    font-size: 12.5px;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease, border-color 0.12s ease;
    text-decoration:none;
    display:inline-flex;
    align-items:center;
    gap: 7px;
    user-select:none;
  }
  .btn:hover{ transform: translateY(-1px); box-shadow: 0 6px 14px rgba(20,22,31,0.08); }
  .btn:active{ transform: translateY(0px); box-shadow: none; }
  .btn:focus-visible{ outline: 2px solid var(--acc-brand); outline-offset: 2px; }

  .btn.primary{
    background: var(--acc-brand);
    border-color: var(--acc-brand);
    color: #fff;
  }
  .btn.primary:hover{ background: var(--acc-brand-dark); border-color: var(--acc-brand-dark); }

  .btn.ghost{
    background: transparent;
  }
  .btn.ghost:hover{ background: var(--acc-bg); }

  .btn.subtle{
    background: var(--acc-bg);
  }

  .acc-h{
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 10px;
    color: var(--acc-muted);
  }

  .acc-paras p{
    margin: 0 0 10px 0;
    color: var(--acc-text);
    font-size: 13px;
    line-height: 1.6;
  }
  .acc-paras p:last-child{ margin-bottom: 0; }

  .acc-benefits{
    display:grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  @media (max-width: 640px){
    .acc-benefits{ grid-template-columns: 1fr; }
  }

  .acc-benefit{
    border: 1px solid var(--acc-border);
    border-radius: 12px;
    padding: 12px 13px;
    background: var(--acc-bg);
  }

  .acc-benefit-title{
    font-size: 12.5px;
    font-weight: 800;
    margin-bottom: 7px;
    color: var(--acc-text);
  }

  .acc-ul{
    margin: 0;
    padding-left: 16px;
    color: #2E3140;
    font-size: 12.5px;
    line-height: 1.55;
  }
  .acc-ul li + li{ margin-top: 5px; }

  .acc-section{
    padding: 18px 22px;
  }
  .acc-section + .acc-section{
    border-top: 1px solid var(--acc-border);
  }

  .acc-contact{
    display:flex;
    align-items:center;
    gap: 7px;
    flex-wrap: wrap;
    color: var(--acc-muted);
    font-size: 12.5px;
  }

  .acc-muted{ color: var(--acc-muted); }

  .acc-dot{ color: #C7C9D4; }

  .acc-linkbtn{
    color: var(--acc-brand);
    font-weight: 700;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
    font-size: 12.5px;
  }
  .acc-linkbtn:hover{ color: var(--acc-brand-dark); }

  .acc-empty{
    margin-top: 18px;
    padding: 18px 20px;
    border: 1px dashed var(--acc-border);
    border-radius: 14px;
    background: #fff;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: 14px;
    flex-wrap: wrap;
  }
  .acc-empty-title{
    font-weight: 800;
    font-size: 14px;
    margin-bottom: 3px;
  }
  .acc-empty-sub{
    color: var(--acc-muted);
    font-size: 13px;
  }

  .acc-toast{
    position: fixed;
    right: 18px;
    bottom: 18px;
    background: var(--acc-text);
    color: #fff;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    box-shadow: 0 10px 30px rgba(2,6,23,0.35);
    z-index: 80;
    animation: acc-toast-in 0.18s ease;
  }

  @keyframes acc-toast-in{
    from{ opacity: 0; transform: translateY(6px); }
    to{ opacity: 1; transform: translateY(0); }
  }

  /* ---------------- Modal ---------------- */

  .acc-modal-overlay{
    position: fixed;
    inset: 0;
    background: rgba(15, 16, 24, 0.50);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 70;
    animation: acc-overlay-in 0.16s ease;
  }

  @keyframes acc-overlay-in{
    from{ opacity: 0; }
    to{ opacity: 1; }
  }

  .acc-modal{
    position: relative;
    top:15px;
    display: flex;
    width: 100%;
    max-width: 720px;
    max-height: min(80vh, 760px);
    background: var(--acc-card);
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 24px 60px -12px rgba(8, 9, 16, 0.45);
    animation: acc-modal-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes acc-modal-in{
    from{ opacity: 0; transform: translateY(10px) scale(0.98); }
    to{ opacity: 1; transform: translateY(0) scale(1); }
  }

  .acc-modal-rail{
    width: 5px;
    flex: 0 0 auto;
    background: var(--rail-color, var(--acc-brand));
  }

  .acc-modal-body{
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    max-height: min(86vh, 760px);
  }

  .acc-modal-head{
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--acc-border);
    flex: 0 0 auto;
  }

  .acc-modal-head-main{ flex: 1 1 auto; min-width: 0; }

  .acc-modal-title{
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.2px;
  }

  .acc-modal-close{
    border: 1px solid var(--acc-border);
    background: #fff;
    color: var(--acc-text);
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 0 0 auto;
    transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  }
  .acc-modal-close svg{
    color: inherit;
    stroke: currentColor;
  }
  .acc-modal-close:hover{ background: var(--acc-bg); color: var(--acc-text); border-color: var(--acc-text); }
  .acc-modal-close:focus-visible{ outline: 2px solid var(--acc-brand); outline-offset: 2px; }

  .acc-modal-scroll{
    overflow-y: auto;
    flex: 1 1 auto;
  }

  .acc-modal-footer{
    display:flex;
    justify-content:space-between;
    align-items:center;
    gap: 12px;
    padding: 14px 20px;
    border-top: 1px solid var(--acc-border);
    background: var(--acc-bg);
    flex-wrap: wrap;
    flex: 0 0 auto;
  }

  .acc-modal-footer-actions{
    display:flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  @media (max-width: 640px){
    .acc-modal-overlay{ padding: 0; align-items: flex-end; }
    .acc-modal{
      max-width: 100%;
      max-height: 92vh;
      border-radius: 18px 18px 0 0;
    }
    .acc-modal-body{ max-height: 92vh; }
  }

  @media (prefers-reduced-motion: reduce){
    .acc-card, .btn, .acc-toast, .acc-modal-overlay, .acc-modal{
      transition: none !important;
      animation: none !important;
    }
  }
`;
